import { ButtonLink } from '@landler/component-library';
import { cn } from 'component-library/utils';
import { memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Organization, OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { LandlerLogo } from '@/components/LandlerLogo/LandlerLogo';
import { LogOutPill } from '@/components/LogOutPill/LogOutPill';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useEffectOnce } from '@/hooks/useEffectOnce';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { useOrganization } from '@/hooks/useOrganization';
import { usePathSet } from '@/hooks/usePathSet';
import { useUIStyle } from '@/hooks/useUIStyle';
import { useUserMe } from '@/hooks/useUserMe';
import { WorkstreamButtonLink } from '@/pages/shared/home/components/WorkstreamButtonLink';
import { useWorkstreamId } from '@/pages/shared/home/hooks/useWorkstreamId';
import { useIsOrganizationMember } from '@/pages/shared/hooks/useIsOrganizationMember';
import { useLimitedAccessOrganization } from '@/pages/shared/hooks/useLimitedAccessOrganization';
import { useNCAParents } from '@/pages/shared/hooks/useNCAParents';
import { useOrganizationId, useSetOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useOrganizationPlotGroups } from '@/pages/shared/hooks/useOrganizationPlotGroup';
import { useUserOrganizationId } from '@/pages/shared/hooks/useUserOrganizationId';
import { useDisplayUploadsPage } from '@/pages/shared/uploads/hooks/useDisplayUploadsPage';
import { useDomainVisibility } from '@/pages/shared/workstreams/pages/hooks/useDomainVisibility';
import { paths } from '@/routing';
import { assertIsDefined } from '@/utils/assert';
import { buildPath } from '@/utils/buildPath';

import { SidebarToggleButton } from '../../a/Button/SidebarToggleButton';
import { Sidebar, SidebarItem } from './Sidebar';

type NavigationSidebarItem = {
  key: string;
  icon: string | ReactNode;
  label: string;
  to?: string;
  children?: SidebarItem[];
  isText?: boolean;
  isActive?: boolean;
  customContent?: ReactNode;
};

type NavigationSidebarProps = {
  isCollapsed: boolean;
  onMinimize: () => void;
};

export const NavigationSidebar = memo(({ isCollapsed, onMinimize }: NavigationSidebarProps) => {
  return <DefaultLandlerSidebar {...{ isCollapsed, onMinimize }} />;
});
NavigationSidebar.displayName = 'NavigationSidebar';

const DefaultLandlerSidebar = ({ isCollapsed, onMinimize }: NavigationSidebarProps) => {
  const navigationItems: NavigationSidebarItem[] = useDefaultNavigationItems();
  const props = getNavigationItems({ navigationItems, isCollapsed, onMinimize });
  return <Sidebar footer={{ content: <LogOutPill /> }} {...props} />;
};

const getNavigationItems = ({
  navigationItems,
  isCollapsed = false,
  onMinimize,
}: {
  navigationItems: NavigationSidebarItem[];
  isCollapsed: boolean;
  onMinimize: () => void;
}) => {
  return {
    header: {
      content: <NavigationSidebarHeader isCollapsed={isCollapsed} onMinimize={onMinimize} />,
      style: 'py-0',
    },
    items: navigationItems.map(
      ({ key, icon, label, to, children, isText = false, isActive = false, customContent }) => {
        if (customContent !== undefined) {
          return { key, content: customContent };
        }
        if (children && children.length > 0) {
          return createSidebarExpandableItem(key, icon, label, children, to, isActive);
        }
        if (isText) {
          return createSideBarTextItem(key, label);
        }

        return createSidebarItem(key, icon, label, to ?? '', isActive);
      },
    ),
    isCollapsed,
  };
};

export const NavigationSidebarHeader = ({ isCollapsed = false, onMinimize }: NavigationSidebarProps) => {
  return (
    <div
      className={cn(
        'flex h-[88px] items-center justify-between border-b border-neutral-black-12 px-6',
        isCollapsed && 'justify-center',
      )}
    >
      <LandlerLogo className={cn(isCollapsed ? 'h-9' : 'h-6')} isCollapsed={isCollapsed} />
      {!isCollapsed && onMinimize && <SidebarToggleButton direction='left' onClick={onMinimize} />}
    </div>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const useDefaultNavigationItems = () => {
  const { t } = useTranslation();
  const user = useUserMe().data;
  const pathSet = usePathSet();
  const isStaffUser = useIsStaffUser();
  const hasConservationUiStyle = useUIStyle(OrganizationUIStyle.CONSERVATION);

  const { pathname, search } = useLocation();
  const currentPath = pathname + search;

  const organizationId = useOrganizationId();
  const setOrganizationId = useSetOrganizationId();
  assertIsDefined(organizationId, 'organizationId');
  const organization = useOrganization({ id: organizationId }).data.results[0];

  const isOrganizationMember = useIsOrganizationMember();
  const plotGroups = useOrganizationPlotGroups(organizationId).data;

  const limitedAccessOrganizations = useLimitedAccessOrganization(organizationId, { user_id: user.id }).data;
  const visiblePlotGroups =
    limitedAccessOrganizations && limitedAccessOrganizations.length > 0
      ? limitedAccessOrganizations.map((lao) => lao.plot_group)
      : plotGroups;

  const userOrganizationId = useUserOrganizationId();
  const ncaParentOrganizations = useNCAParents(userOrganizationId).data;

  const domainVisibility = useDomainVisibility();

  // Set organization to first NCA parent if current org has 0 plots and NCA parents exist
  const orgHasNoPlots = organization && organization.plots_count === 0;
  const orgHasNCAParents =
    ncaParentOrganizations.length > 0 &&
    ncaParentOrganizations[0]?.id &&
    organizationId !== ncaParentOrganizations[0].id;
  useEffectOnce(() => {
    if (orgHasNoPlots && orgHasNCAParents && ncaParentOrganizations[0]?.id) {
      setOrganizationId(ncaParentOrganizations[0]?.id);
    }
  }, [organization, ncaParentOrganizations, organizationId, setOrganizationId]);

  const getOrganizationsSidebarItems = (organizations: Organization[]): SidebarItem[] => {
    const plotsBasePath = pathSet.home;
    return [
      ...organizations.map((org) =>
        createSidebarItem(
          `org${org.id}`,
          'ri-hotel-line',
          org.name ?? '',
          `${plotsBasePath}?organizationId=${org.id}`,
          currentPath === `${plotsBasePath}?organizationId=${org.id}`,
          () => setOrganizationId(org.id),
        ),
      ),
    ];
  };

  const displayeUploadsPage = useDisplayUploadsPage();

  const asInvestorSidebarItems =
    ncaParentOrganizations.length > 0
      ? [
          createSideBarTextItem('asInvestor', t('navbar.organizations.asInvestor')),
          ...getOrganizationsSidebarItems(ncaParentOrganizations),
        ]
      : [];

  const byPermissionSidebarItems = [
    createSideBarTextItem('byPermission', t('navbar.organizations.byPermission')),
    ...getOrganizationsSidebarItems([user.membership.organization ?? organization]),
  ];

  const [searchParams] = useSearchParams();
  const plotGroupQueryParams = useMemo(() => {
    const queryParamPlotGroupId = searchParams.get('plotGroupId');
    return queryParamPlotGroupId ? { queryParams: { plotGroupId: queryParamPlotGroupId } } : undefined;
  }, [searchParams]);

  const workstreamId = useWorkstreamId();
  const workstreamQueryParams = workstreamId ? { queryParams: { workstreamId } } : undefined;

  const allQueryParams =
    plotGroupQueryParams || workstreamQueryParams
      ? { queryParams: { ...(plotGroupQueryParams?.queryParams ?? {}), ...(workstreamQueryParams?.queryParams ?? {}) } }
      : undefined;

  const plotGroupsSidebarItems =
    visiblePlotGroups.length > 0 && isOrganizationMember && !hasConservationUiStyle
      ? [
          createSidebarItem(
            'groups',
            'ri-stack-line',
            t('navbar.groups'),
            buildPath(pathSet.groups, workstreamQueryParams),
            currentPath === buildPath(pathSet.groups, workstreamQueryParams),
          ),
        ]
      : [];

  const landPageChildren = hasConservationUiStyle
    ? undefined
    : [
        createSidebarItem(
          'plots',
          'ri-map-pin-line',
          t('navbar.plots'),
          buildPath(pathSet.plots, allQueryParams),
          currentPath === buildPath(pathSet.plots, allQueryParams),
        ),
        ...plotGroupsSidebarItems,
      ];

  const portfolioSectionChildren = hasConservationUiStyle
    ? [
        createSidebarItem(
          'natureCapital',
          'ri-leaf-line',
          t('navbar.natureCapital'),
          pathSet.portfolioNaturalCapital,
          currentPath === pathSet.portfolioNaturalCapital,
        ),
        createSidebarItem(
          'stocks',
          'ri-bar-chart-2-line',
          t('navbar.stocks'),
          pathSet.portfolioStocks,
          currentPath === pathSet.portfolioStocks,
        ),
        createSidebarItem(
          'contracts',
          'ri-contract-line',
          t('navbar.contracts'),
          pathSet.portfolioContracts,
          currentPath === pathSet.portfolioContracts,
        ),
      ]
    : [
        createSidebarItem(
          'natureCapital',
          'ri-leaf-line',
          t('navbar.natureCapital'),
          pathSet.portfolioNaturalCapital,
          currentPath === pathSet.portfolioNaturalCapital,
        ),
        createSidebarItem(
          'forecasts',
          'ri-line-chart-line',
          t('navbar.forecasts'),
          pathSet.portfolioForecasts,
          currentPath === pathSet.portfolioForecasts,
        ),
        createSidebarItem(
          'investments',
          'ri-funds-line',
          t('navbar.investments'),
          pathSet.portfolioInvestments,
          currentPath === pathSet.portfolioInvestments,
        ),
        createSidebarItem(
          'units',
          'ri-stack-line',
          t('navbar.units'),
          pathSet.portfolioUnits,
          currentPath === pathSet.portfolioUnits,
        ),
        createSidebarItem(
          'stocks',
          'ri-bar-chart-2-line',
          t('navbar.stocks'),
          pathSet.portfolioStocks,
          currentPath === pathSet.portfolioStocks,
        ),
        createSidebarItem(
          'contracts',
          'ri-contract-line',
          t('navbar.contracts'),
          pathSet.portfolioContracts,
          currentPath === pathSet.portfolioContracts,
        ),
      ];

  return [
    {
      key: 'activeOrganization',
      label: organization?.name ?? t('global.organization.organizations'),
      icon: 'ri-hotel-line',
      to: '',
      isActive: false,
      children: [...asInvestorSidebarItems, ...byPermissionSidebarItems],
    },
    {
      key: 'home',
      icon: 'ri-home-5-line',
      label: t('navbar.home'),
      to: pathSet.home,
      isActive: hasConservationUiStyle && currentPath === pathSet.home,
    },
    {
      key: 'workstreamButtonLink',
      icon: null,
      label: '',
      customContent: <WorkstreamButtonLink />,
    },
    ...(hasConservationUiStyle
      ? []
      : [
          {
            key: 'land',
            icon: 'ri-landscape-line',
            label: t('navbar.land'),
            children: landPageChildren,
          },
        ]),
    ...(!hasConservationUiStyle && Object.values(domainVisibility).some((isVisible) => isVisible)
      ? [
          {
            key: 'measurements',
            icon: 'ri-pulse-line',
            label: t('navbar.measurements'),
            children: [
              ...(domainVisibility.carbon
                ? [
                    createSidebarItem(
                      'carbon',
                      'ri-cloud-line',
                      t('navbar.carbon'),
                      buildPath(pathSet.carbonDetails, allQueryParams),
                      currentPath === buildPath(pathSet.carbonDetails, allQueryParams),
                    ),
                  ]
                : []),
              ...(domainVisibility.water
                ? [
                    createSidebarItem(
                      'water',
                      'ri-drop-line',
                      t('navbar.water'),
                      buildPath(pathSet.waterDetails, allQueryParams),
                      currentPath === buildPath(pathSet.waterDetails, allQueryParams),
                    ),
                  ]
                : []),
              ...(domainVisibility.biodiversity
                ? [
                    createSidebarItem(
                      'biodiversity',
                      'ri-plant-line',
                      t('navbar.biodiversity'),
                      buildPath(pathSet.biodiversityDetails, allQueryParams),
                      currentPath === buildPath(pathSet.biodiversityDetails, allQueryParams),
                    ),
                  ]
                : []),
              ...(domainVisibility.soil
                ? [
                    createSidebarItem(
                      'soil',
                      'ri-seedling-line',
                      t('navbar.soil'),
                      buildPath(pathSet.soilDetails, allQueryParams),
                      currentPath === buildPath(pathSet.soilDetails, allQueryParams),
                    ),
                  ]
                : []),
              ...(domainVisibility.social
                ? [
                    createSidebarItem(
                      'social',
                      'ri-team-line',
                      t('navbar.social'),
                      buildPath(pathSet.socialDetails, allQueryParams),
                      currentPath === buildPath(pathSet.socialDetails, allQueryParams),
                    ),
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(hasConservationUiStyle
      ? []
      : [
          {
            key: 'intelligenceTools',
            icon: 'ri-tools-line',
            label: t('navbar.intelligenceTools'),
            children: [
              createSidebarItem(
                'compare',
                'ri-scales-3-line',
                t('navbar.comparisons'),
                buildPath(pathSet.comparisons, workstreamQueryParams),
                currentPath === buildPath(pathSet.comparisons, workstreamQueryParams),
              ),
              ...(!workstreamId
                ? [
                    createSidebarItem(
                      'scenario-models',
                      'ri-flow-chart',
                      t('navbar.scenarioModelling'),
                      pathSet.scenarioModels,
                      currentPath === pathSet.scenarioModels,
                    ),
                    createSidebarItem(
                      'reports',
                      'ri-file-list-3-line',
                      t('navbar.reports'),
                      buildPath(pathSet.reports, workstreamQueryParams),
                      currentPath === buildPath(pathSet.reports, workstreamQueryParams),
                    ),
                  ]
                : []),
              createSidebarItem(
                'copilot',
                'ri-sparkling-2-line',
                t('navbar.va'),
                buildPath(pathSet.copilot, workstreamQueryParams),
                currentPath === buildPath(pathSet.copilot, workstreamQueryParams),
              ),
            ],
          },
        ]),

    ...(!workstreamId
      ? [
          {
            key: 'portfolio',
            icon: 'ri-line-chart-line',
            label: t('navbar.portfolio'),
            to: undefined,
            children: [...portfolioSectionChildren],
          },
        ]
      : []),
    // TODO: MVP-CW bring these pages to the menu or completely remove them and their routes + components
    // { key: 'contracts', icon: 'ri-file-3-line', label: t('navbar.contracts'), to: pathSet.contracts, isActive: currentPath === pathSet.contracts },
    ...(!workstreamId
      ? [
          {
            key: 'settings',
            icon: 'ri-settings-3-line',
            label: t('navbar.settings'),
            children: [
              ...(displayeUploadsPage
                ? [
                    createSidebarItem(
                      'uploads',
                      'ri-folder-add-line',
                      t('navbar.uploads'),
                      pathSet.uploads,
                      currentPath === pathSet.uploads,
                    ),
                  ]
                : []),
              createSidebarItem(
                'profile',
                'ri-user-line',
                t('navbar.profile'),
                pathSet.account,
                currentPath === pathSet.account,
              ),
              ...(isStaffUser // TODO: MVP-9367 enable page for all the users
                ? [
                    createSidebarItem(
                      'workstreams',
                      'ri-flag-line',
                      t('navbar.workstreams'),
                      pathSet.workstreams,
                      currentPath === pathSet.workstreams,
                    ),
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(isStaffUser && !workstreamId
      ? [
          {
            key: 'ndi',
            icon: 'ri-database-2-line',
            label: t('navbar.ndi'),
            children: [
              createSidebarItem(
                'ndiOrganizations',
                'ri-building-line',
                t('global.organization.organizations'),
                paths.staff.organizations,
                currentPath === paths.staff.organizations,
              ),
              createSidebarItem(
                'ndiProducts',
                'ri-price-tag-3-line',
                t('navbar.products'),
                paths.staff.ndiProducts,
                currentPath === paths.staff.ndiProducts,
              ),
            ],
          },
        ]
      : []),
  ];
};

const createSidebarExpandableItem = (
  key: string,
  icon: string | ReactNode,
  label: string,
  children: SidebarItem[],
  to?: string,
  isActive?: boolean,
): SidebarItem => ({
  key,
  content: to ? (
    <ButtonLink
      to={to}
      variant='text'
      className={cn(
        'text-text-primary justify-start grow rounded-md hover:rounded-md text-left leading-[130%] hover:bg-transparent',
        !icon && 'text-text-secondary',
      )}
    >
      {typeof icon === 'string' ? <Icon className='text-2xl/none' icon={icon} /> : icon}
      <span>{label}</span>
    </ButtonLink>
  ) : (
    <div
      className={cn('flex items-center gap-2 w-full rounded-md text-left leading-[130%] px-5 py-3 typography-button1')}
    >
      {typeof icon === 'string' ? <Icon className='text-2xl/none' icon={icon} /> : icon}
      <span>{label}</span>
    </div>
  ),
  children,
  isActive,
});

const createSidebarItem = (
  key: string,
  icon: string | ReactNode,
  label: string,
  to: string,
  isActive: boolean,
  onClick?: () => void,
): SidebarItem => ({
  key,
  content: (
    <ButtonLink
      variant='text'
      to={to}
      className={cn(
        'text-text-primary justify-start w-full rounded-md hover:rounded-md text-left leading-[130%]',
        !icon && 'text-text-secondary',
        isActive && 'bg-neutral-hover text-text-primary',
      )}
      onClick={onClick}
    >
      {typeof icon === 'string' ? <Icon className='text-2xl/none' icon={icon} /> : icon}
      <span>{label}</span>
    </ButtonLink>
  ),
});

const createSideBarTextItem = (key: string, label: string): SidebarItem => ({
  key,
  content: <span className='typography-overline uppercase px-5 pt-4'>{label}</span>,
});
